import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api';
import { 
  Lock, LogOut, Briefcase, Newspaper, Languages, FileText, 
  UserCircle, Truck, Settings, Clock, HelpCircle, Globe, Home, Award, Inbox
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';

// Import all modular admin components
import ServicesManager from '../components/admin/ServicesManager';
import FAQManager from '../components/admin/FAQManager';
import FleetManager from '../components/admin/FleetManager';
import DestinationsManager from '../components/admin/DestinationsManager';
import ContactManager from '../components/admin/ContactManager';
import TimelineManager from '../components/admin/TimelineManager';
import CareersManager from '../components/admin/CareersManager';
import NewsManager from '../components/admin/NewsManager';
import LegalManager from '../components/admin/LegalManager';
import HomeManager from '../components/admin/HomeManager';
import WhyChooseUsManager from '../components/admin/WhyChooseUsManager';
import SiteContentManager from '../components/admin/SiteContentManager';
import SettingsManager from '../components/admin/SettingsManager';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_URL}/api`;

// Login Component
function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Set page title for admin
  usePageTitle('admin');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await apiClient.post(`${API}/admin/login`, { password });
      localStorage.setItem('adminAuth', 'true');
      onLogin();
      toast.success('Login successful!');
    } catch (error) {
      // 429 errors are handled by interceptor
      if (error.response?.status !== 429) {
        toast.error('Invalid password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-white rounded-3xl shadow-xl">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#C41E3A]" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Panel</h1>
            <p className="text-slate-500 text-sm mt-2">Enter your password to continue</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-600">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 bg-slate-50 border-slate-200 rounded-xl"
                placeholder="Enter admin password"
                required
              />
            </div>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="w-full bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full py-6"
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
// Main Admin Dashboard
export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-500">Manage site content in English and Bulgarian</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="rounded-full">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="home">
          <TabsList className="bg-white rounded-2xl p-2 mb-8 flex flex-wrap gap-2 h-auto">
            <TabsTrigger value="home" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Home className="w-4 h-4 mr-1 sm:mr-2" /> Home
            </TabsTrigger>
            <TabsTrigger value="why-choose-us" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Award className="w-4 h-4 mr-1 sm:mr-2" /> Why Us
            </TabsTrigger>
            <TabsTrigger value="content" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Languages className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Site </span>Content
            </TabsTrigger>
            <TabsTrigger value="services" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Settings className="w-4 h-4 mr-1 sm:mr-2" /> Services
            </TabsTrigger>
            <TabsTrigger value="fleet" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Truck className="w-4 h-4 mr-1 sm:mr-2" /> Fleet
            </TabsTrigger>
            <TabsTrigger value="destinations" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Globe className="w-4 h-4 mr-1 sm:mr-2" /> Destinations
            </TabsTrigger>
            <TabsTrigger value="timeline" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Clock className="w-4 h-4 mr-1 sm:mr-2" /> About Us
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <UserCircle className="w-4 h-4 mr-1 sm:mr-2" /> Contact
            </TabsTrigger>
            <TabsTrigger value="careers" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Briefcase className="w-4 h-4 mr-1 sm:mr-2" /> Careers
            </TabsTrigger>
            <TabsTrigger value="news" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Newspaper className="w-4 h-4 mr-1 sm:mr-2" /> News
            </TabsTrigger>
            <TabsTrigger value="faq" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <HelpCircle className="w-4 h-4 mr-1 sm:mr-2" /> FAQ
            </TabsTrigger>
            <TabsTrigger value="legal" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <FileText className="w-4 h-4 mr-1 sm:mr-2" /> Legal
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Inbox className="w-4 h-4 mr-1 sm:mr-2" /> Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="home">
            <HomeManager />
          </TabsContent>

          <TabsContent value="why-choose-us">
            <WhyChooseUsManager />
          </TabsContent>

          <TabsContent value="content">
            <SiteContentManager />
          </TabsContent>

          <TabsContent value="services">
            <ServicesManager />
          </TabsContent>

          <TabsContent value="fleet">
            <FleetManager />
          </TabsContent>

          <TabsContent value="destinations">
            <DestinationsManager />
          </TabsContent>

          <TabsContent value="timeline">
            <TimelineManager />
          </TabsContent>

          <TabsContent value="contacts">
            <ContactManager />
          </TabsContent>

          <TabsContent value="careers">
            <CareersManager />
          </TabsContent>

          <TabsContent value="news">
            <NewsManager />
          </TabsContent>

          <TabsContent value="faq">
            <FAQManager />
          </TabsContent>

          <TabsContent value="legal">
            <LegalManager />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
